import { Link, router } from "@inertiajs/react"
import { useState } from "react"
import type { ReactNode } from "react"
import { FiBarChart2, FiCamera, FiCheck, FiChevronDown, FiClock, FiEdit3, FiFeather, FiHelpCircle, FiImage, FiLayout, FiMessageCircle, FiMoreHorizontal, FiPhone, FiShare2 } from "react-icons/fi"

type EditorAction = {
  key: string
  label: string
  href: string
  icon: ReactNode
  external?: boolean
  method?: "patch"
}

type Props = {
  cardId: number
  customerMode: boolean
  activeSection: string
  analyticsHref: string
  actions?: EditorAction[]
}

const sections = [
  ["overview", "פרטים", <FiEdit3 aria-hidden="true" />],
  ["contact", "פרטים כלליים", <FiPhone aria-hidden="true" />],
  ["social", "רשתות חברתיות", <FiShare2 aria-hidden="true" />],
  ["buttons", "כפתורי פעולה", <FiMoreHorizontal aria-hidden="true" />],
  ["design", "עיצוב", <FiFeather aria-hidden="true" />],
  ["structure", "סדר הכרטיס", <FiLayout aria-hidden="true" />],
  ["opening_hours", "שעות פתיחה", <FiClock aria-hidden="true" />],
  ["faq", "שאלות ותשובות", <FiHelpCircle aria-hidden="true" />],
  ["media", "תמונות", <FiImage aria-hidden="true" />],
  ["gallery", "גלריה", <FiCamera aria-hidden="true" />],
  ["engagement", "המלצות", <FiMessageCircle aria-hidden="true" />],
] as const

export function CardEditorNav({ cardId, customerMode, activeSection, analyticsHref, actions = [] }: Props) {
  const editBase = customerMode ? `/dash/card/${cardId}/edit` : `/admin/cards/${cardId}/edit`
  const [menuOpen, setMenuOpen] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const activeLabel = sections.find(([key]) => key === activeSection)?.[1] ?? "פרטים"
  const sectionPath = (key: string) => key === "gallery"
    ? (customerMode ? `/dash/card/${cardId}/gallery` : `/admin/cards/${cardId}/gallery`)
    : key === "opening_hours"
      ? (customerMode ? `/dash/card/${cardId}/opening-hours` : `/admin/cards/${cardId}/opening_hours`)
    : `${editBase}/${key}`

  const visitAction = (action: EditorAction) => {
    if (action.method === "patch") router.patch(action.href, {}, { preserveScroll: true })
    else router.visit(action.href)
  }

  const actionItems = (close: () => void) => actions.map((action) => action.external ? (
    <a key={action.key} href={action.href} target="_blank" rel="noreferrer" role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-slate-700 hover:bg-slate-50" onClick={close}>
      {action.icon}{action.label}
    </a>
  ) : (
    <button key={action.key} type="button" role="menuitem" className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-right text-sm text-slate-700 hover:bg-slate-50" onClick={() => { close(); visitAction(action) }}>
      {action.icon}{action.label}
    </button>
  ))

  return (
    <nav aria-label="ניווט עריכת כרטיס" className="relative z-30 mb-6">
      <div className="relative md:hidden">
        <button
          type="button"
          className="input-base flex items-center justify-between gap-3 text-right"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span>{activeLabel}</span>
          <FiChevronDown className={`shrink-0 text-slate-500 transition-transform ${menuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {menuOpen ? (
          <div className="absolute inset-x-0 z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" role="menu">
            {sections.map(([key, label, icon]) => (
              <button
                key={key}
                type="button"
                role="menuitem"
                className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-right text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setMenuOpen(false)
                  router.visit(sectionPath(key))
                }}
              >
                <span className="flex items-center gap-3">{icon}{label}</span>
                {activeSection === key ? <FiCheck className="text-brand-600" aria-hidden="true" /> : null}
              </button>
            ))}
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-right text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setMenuOpen(false)
                router.visit(analyticsHref)
              }}
            >
              <FiBarChart2 aria-hidden="true" />ביצועים
            </button>
            {actions.length > 0 ? <>
              <div className="my-1 border-t border-slate-100" />
              {actionItems(() => setMenuOpen(false))}
            </> : null}
          </div>
        ) : null}
      </div>
      <div className="hidden flex-wrap gap-2 pb-1 md:flex">
        {sections.map(([key, label, icon]) => (
          <Link
            key={key}
            href={sectionPath(key)}
            className={`whitespace-nowrap rounded-2xl px-4 py-2.5 text-sm font-medium transition ${
              activeSection === key ? "bg-brand-600 text-white shadow-soft" : "bg-white text-slate-700 shadow-sm hover:bg-slate-50"
            }`}
          >
            <span className="flex items-center gap-2">{icon}{label}</span>
          </Link>
        ))}
        <Link href={analyticsHref} className="flex items-center gap-2 whitespace-nowrap rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50">
          <FiBarChart2 aria-hidden="true" />ביצועים
        </Link>
        {actions.length > 0 ? <div className="relative">
          <button type="button" className="btn-secondary gap-2 px-4 py-2.5 text-sm" aria-haspopup="menu" aria-expanded={actionsOpen} onClick={() => setActionsOpen((open) => !open)}>
            <FiMoreHorizontal aria-hidden="true" />עוד
          </button>
          {actionsOpen ? <div className="absolute end-0 z-50 mt-2 min-w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" role="menu">
            {actionItems(() => setActionsOpen(false))}
          </div> : null}
        </div> : null}
      </div>
    </nav>
  )
}
